/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ValueContainerControllerBase", ["ControllerBase"], function (context, cls) {
  /**
   * Base controller for an AUI node.
   * Manages client side life cycle representation of the node.
   * @class ValueContainerControllerBase
   * @memberOf classes
   * @extends classes.ControllerBase
   */
  class ValueContainerControllerBase extends cls.ControllerBase /** @lends classes.ValueContainerControllerBase.prototype */ {
    /**
     * @inheritDoc
     */
    _initBehaviors() {
      super._initBehaviors();
      if (this.isInStack() && !(this.isInMatrix() || this.isInTable())) {
        this._addBehavior(cls.StackLabelVMBehavior);
      }
    }

    /**
     * Creates a new widget depending on the dialog type
     * @returns {classes.WidgetBase} the widget
     */
    createWidget() {
      if (!this._widget && this.autoCreateWidget()) {
        let dialogType = null;

        // Determine the widget kind of a valueNode
        if (this.getAnchorNode()) {
          dialogType = this.getAnchorNode().attribute("dialogType");
        }
        if (!dialogType && this.getNodeBindings().decorator) {
          dialogType = this.getNodeBindings().decorator.attribute("dialogType");
        }
        if (!dialogType && this.getNodeBindings().container) {
          dialogType = this.getNodeBindings().container.attribute("dialogType");
        }

        const type = this._getWidgetType(dialogType);

        this._widgetKind = dialogType;
        this._widgetType = type;

        this._widget = this._createWidget(type);
      }
      return this._widget;
    }

    /**
     *
     * @inheritDoc
     * @protected
     * @virtual
     */
    _createWidget(type) {
      return cls.WidgetFactory.createWidget(
        type,
        {
          appHash: this.getAnchorNode().getApplication().applicationHash,
          appWidget: this.getAnchorNode().getApplication().getUI().getWidget(),
          auiTag: this.getAnchorNode().getId(),
          inTable: this.isInTable(),
          inFirstTableRow: this.isInFirstTableRow(),
          inMatrix: this.isInMatrix(),
          inScrollGrid: this.isInScrollGrid()
        },
        this.getNodeBindings().decorator
      );
    }

    /**
     * Strategy method which returns widget value in VM ready format
     * @returns {string} the widget value
     * @protected
     */
    getWidgetValue(newValue = null) {
      const decoratorNode = this.getNodeBindings().decorator;
      const widget = this.getWidget();
      let value = newValue !== null ? newValue : widget.getValue();

      if (value === null || value === undefined) {
        value = "";
      } else {
        value = value.toString();
      }
      value = this._shiftConversion(value, widget, decoratorNode);
      return value;
    }

    /**
     * Strategy method which returns AUI value in VM ready format
     * @returns {string} the AUI value
     */
    getAuiValue() {
      const valueNode = this.getNodeBindings().anchor;
      return valueNode.attribute("value").toString();
    }

    /**
     * Get the value depending on the shift attribute
     * @param {string} value - value to process
     * @param {classes.WidgetBase} widget - concerned widget
     * @param {classes.NodeBase} decoratorNode - concerned Node
     * @return {string} - the updated value
     * @private
     */
    _shiftConversion(value, widget, decoratorNode) {
      // manage upshift & downshift case
      if (decoratorNode && decoratorNode.isAttributeSetByVM("shift")) {
        let shiftAttr = decoratorNode.attribute("shift");
        if (this.getWidget() && this.getWidget().getTextTransform) {
          shiftAttr = this.getWidget().getTextTransform();
        }
        if (shiftAttr !== "none" && widget.isEditing && widget.isEditing()) {
          switch (shiftAttr) {
            case "up":
              value = value.toUpperCase();
              break;
            case "down":
              value = value.toLowerCase();
              break;
          }
        }
      }
      return value;
    }

    /**
     * @inheritDoc
     */
    sendWidgetValue(newValue = null) {
      const anchorNode = this.getAnchorNode();
      const widgetValue = this.getWidgetValue(newValue);
      anchorNode.getApplication().scheduler.valueVMCommand(anchorNode, widgetValue);
    }

    /**
     * @inheritDoc
     */
    sendWidgetCursors() {
      const widget = this.getWidget();
      if (!widget || !widget.hasCursors()) {
        return;
      }
      const cursors = widget.getCursors();
      const anchorNode = this.getAnchorNode();
      const widgetValueLength = this.getWidgetValue().length;
      anchorNode.getApplication().scheduler.cursorsVMCommand(anchorNode, cursors.start, cursors.end, widgetValueLength);
    }

    /**
     * @inheritDoc
     */
    hasActiveDropDown() {
      if (this._widget) {
        return Boolean(this._widget.getDropDown) && Boolean(this._widget.getDropDown()) && this._widget.getDropDown().isVisible();
      }
      return super.hasActiveDropDown();
    }
  }

  cls.ValueContainerControllerBase = ValueContainerControllerBase;
  ValueContainerControllerBase.prototype.__name = "ValueContainerControllerBase";
});
